import { useEffect } from 'react';
import Layout from '../../../components/layout/Layout';
import Breadcrumb from '../../../components/layout/Breadcrumb';
import { updateSEO, seoData } from '../../../utils/seo';

// ===== 執事の危機管理指針（route=/standards/crisis）=====
// デザイン土台: 紺#1a2942 / 真鍮金#a38e65 / BTN-08 / 紺帯番号バンド / 白リッチカード / 細罫エディトリアル行
// /butler/become や /standards/grooming と同型セクションの使い回しはしない（構成を変えた新バリエーション）
// 事実正典: 設立1890年（明治23年）/ 代表理事 石井裕一（三代目頭取）/
//   資格は初級Associate・中級Professional・上級Masterの3段階+専門資格
// 創作した年号・数値・実績・事故件数は書かない
// 医療行為や法的判断を執事が行うとは書かない

export default function StandardsCrisisPage() {
  useEffect(() => {
    updateSEO(seoData['/standards/crisis']);
  }, []);

  const breadcrumbs = [
    { label: 'ホーム', href: '/' },
    { label: '基準・指針', href: '/standards' },
    { label: '執事の危機管理指針' }
  ];

  const BTN08_ON_LIGHT =
    "relative inline-flex items-center justify-center bg-[#1a2942] text-[#f8f6f0] border border-[#a38e65] px-[42px] py-[16px] text-sm tracking-[0.2em] font-['Noto_Serif_JP',serif] transition-colors duration-300 hover:bg-[#a38e65] hover:text-[#1a2942] hover:border-[#1a2942] cursor-pointer whitespace-nowrap before:content-[''] before:absolute before:inset-1 before:border before:border-[#a38e65]/45 before:pointer-events-none before:transition-colors before:duration-300 hover:before:border-[#1a2942]/45";
  const BTN08_ON_NAVY =
    "relative inline-flex items-center justify-center bg-[#a38e65] text-white font-semibold border border-[#1a2942] px-[42px] py-[16px] text-sm tracking-[0.2em] font-['Noto_Serif_JP',serif] transition-colors duration-300 hover:bg-[#1a2942] hover:text-[#f8f6f0] hover:border-[#a38e65] cursor-pointer whitespace-nowrap before:content-[''] before:absolute before:inset-1 before:border before:border-[#1a2942]/40 before:pointer-events-none before:transition-colors before:duration-300 hover:before:border-[#a38e65]/50";

  const situationAxes = [
    {
      en: 'Illness',
      ja: '急病・体調急変',
      text: '依頼者や来客、同居する方の様子が明らかに平時と異なるとき。症状の診断や処置は行わず、安全な姿勢の確保と、あらかじめ定められた連絡先への速やかな取次を本体とします。'
    },
    {
      en: 'Fire',
      ja: '火災・煙・異臭',
      text: '煙・熱・焦げた臭いを感じた瞬間から、消火より避難と通報の判断を先に置きます。小さな炎でも一人で抱え込まず、家の動線と集合場所の取り決めに従います。'
    },
    {
      en: 'Intruder',
      ja: '不審者・不審物',
      text: '門扉・通用口・敷地内での違和感は、対峙や追及ではなく距離の確保と記録、そして定められた連絡系統への報告が先です。鍵と視線の管理を平時から整えておきます。'
    },
    {
      en: 'Outage',
      ja: '停電・設備停止',
      text: '照明・空調・通信・昇降機の停止は、暗所での転倒や閉じ込めを招きます。懐中電灯と非常口の確認、温度管理が必要な保管物の扱いを、あわてず順に進めます。'
    },
    {
      en: 'Leak',
      ja: '水漏れ・浸水',
      text: '天井・壁・床下からの漏水は、電源と滑りやすい床面への配慮が先です。止水の可否は設備の範囲内に限り、専門業者と家側の担当へ速やかに橋渡しします。'
    }
  ];

  const prioritySteps = [
    {
      no: '01',
      title: '人命の安全',
      text: 'その場にいるすべての人の退避・安否・二次被害の回避を最優先とします。物の保全や説明の準備は、人が安全な位置に移ってから着手します。'
    },
    {
      no: '02',
      title: '通報と取次',
      text: '消防・救急・警備など、事態に応じた外部機関と、家があらかじめ指定した連絡先へ取り次ぎます。執事が独自に診断や法的断定を行うことはありません。'
    },
    {
      no: '03',
      title: '被害の保全',
      text: '延焼・浸水・盗難の拡大を抑える範囲で、電源・水道・開口部を扱います。証拠や状況が後から追えるよう、不用意に現場を整えすぎない節度も保ちます。'
    },
    {
      no: '04',
      title: '報告と共有',
      text: '時刻・場所・観察した事実・実施した連絡を、短い言葉で残し、関係者へ共有します。推測と事実を混ぜないことが、その後の判断を支えます。'
    }
  ];

  const absenceGuides = [
    {
      label: '事前の委任範囲',
      lines: [
        '緊急時に連絡する順位と、代理判断を頼める方の有無を平時に確認する',
        '鍵・金庫・車両・ペットなど、触れられる範囲と触れられない範囲を文書で揃える',
        '医療情報や契約の詳細は預かりすぎず、必要な取次先だけを把握する'
      ]
    },
    {
      label: 'その場の判断軸',
      lines: [
        '人命と公共の安全に関わることは、不在を理由に遅らせない',
        '原状を大きく変える処置や高額な発注は、可能な限り承認を取る',
        '連絡が取れない場合の暫定措置を、家ごとの取り決めに沿って選ぶ'
      ]
    },
    {
      label: '戻られたあとの説明',
      lines: [
        '実施したこと・見送ったこと・未解決の点を時系列で短く伝える',
        '感情的な評価を挟まず、観察した事実と残課題を分ける',
        '次に更新すべき連絡先や備品があれば、その場で提案のメモを残す'
      ]
    }
  ];

  const recordPoints = [
    {
      en: 'Channel',
      ja: '連絡系統',
      text: '第一連絡・第二連絡・外部専門業者・近隣協力の順を、紙と共有可能な形の両方で見える化します。個人の記憶や私有端末だけに依存しません。'
    },
    {
      en: 'Fact Log',
      ja: '事実の記録',
      text: '何時に何を見聞きし、誰へ何を伝えたかを、短い文で残します。推測・評価・噂は別欄にし、後から読み返す人が迷わない形を保ちます。'
    },
    {
      en: 'Handover',
      ja: '引継ぎの型',
      text: '当番の交代時には、未完了の連絡と現場の状態を口頭とメモの二重で渡します。危機の途中で人が変わっても、優先順位が途切れないことが目的です。'
    }
  ];

  const peacetimeDrills = [
    {
      en: 'Route',
      ja: '退避経路の確認',
      text: '非常口・階段・集合場所を、暗がりや来客時でも辿れるか定期的に歩いて確かめます。物の仮置きが経路を塞いでいないかも併せて見ます。'
    },
    {
      en: 'Equipment',
      ja: '設備と備品の点検',
      text: '消火器・感知器・懐中電灯・予備電源・止水位置など、触れる頻度の低いものほど点検日を決め、動作と期限表示を静かに確認します。'
    },
    {
      en: 'Roles',
      ja: '役割の再確認',
      text: '誰が通報し、誰が誘導し、誰が記録するかを、資格の段階に応じた現場の編成のなかで共有します。名目だけの担当にしないことが訓練の核です。'
    },
    {
      en: 'Signal',
      ja: '合図と静かな連携',
      text: '大声や慌てた身振りが場を乱す場面では、短い合図と視線の合意で動きます。平時のブリーフィングで、言葉少なな連携の型を育てます。'
    }
  ];

  const reviewLoops = [
    '事実の時系列を、関係者の記憶が新しいうちに突き合わせる',
    'うまくいった初動と、遅れた判断の分岐点を責めずに切り分ける',
    '連絡先・鍵・備品・掲示のどれを更新すべきか具体名で挙げる',
    '本指針や家ごとの手順書へ、変更点を短い追記として戻す',
    'AssociateからMasterまで、同じ事例を教材として静かに共有する'
  ];

  const faqItems = [
    {
      q: '急病の場面で、執事はどこまで関わってよいのですか？',
      a: '安否の確認、安全な場所への誘導、あらかじめ指定された連絡先や救急への取次、周囲の二次被害の防止までが基本です。症状の診断・投薬・医療的な処置は行いません。依頼者やご家族が定めた情報の範囲で、到着する支援へ正確に状況を渡すことが役割です。'
    },
    {
      q: '依頼者と連絡が取れないとき、どこまで独断で進めてよいですか？',
      a: '人命と公共の安全に直結する退避・通報・拡大防止は遅らせません。一方で、原状を大きく変える工事や高額な発注、個人情報に踏み込む開示は、事前の委任範囲と第二連絡先の指示を確認してから進めます。暫定措置を取った場合は、理由と範囲を必ず記録します。'
    },
    {
      q: '記録はどの粒度で残すのが適切ですか？',
      a: '時刻、場所、観察した事実、実施した連絡、未完了の事項が、第三者が読んでも追える粒度を目安とします。感想や推測は分けて書き、情報セキュリティの観点からも、共有範囲と保管場所を必要最小限に保ちます。詳細は情報取り扱いの基準と併せて整えます。'
    }
  ];

  const articleLd = {
    '@context': 'https://schema.org',
    '@type': 'Article',
    headline: '執事の危機管理指針｜全日本執事協会が定める基準',
    description:
      '全日本執事協会が定める執事の危機管理指針。基準化の理由、想定する事態、初動の優先順位、依頼者不在時の判断、連絡系統と記録、平時の訓練と設備点検、事後の振り返り。',
    author: {
      '@type': 'Organization',
      name: '一般社団法人 全日本執事協会'
    },
    publisher: {
      '@type': 'Organization',
      name: '一般社団法人 全日本執事協会'
    },
    mainEntityOfPage: {
      '@type': 'WebPage',
      '@id': 'https://japanbutlerassociation.com/standards/crisis'
    }
  };

  const breadcrumbLd = {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: [
      { '@type': 'ListItem', position: 1, name: 'ホーム', item: 'https://japanbutlerassociation.com/' },
      { '@type': 'ListItem', position: 2, name: '基準・指針', item: 'https://japanbutlerassociation.com/standards' },
      { '@type': 'ListItem', position: 3, name: '執事の危機管理指針', item: 'https://japanbutlerassociation.com/standards/crisis' }
    ]
  };

  const faqLd = {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: faqItems.map((item) => ({
      '@type': 'Question',
      name: item.q,
      acceptedAnswer: {
        '@type': 'Answer',
        text: item.a
      }
    }))
  };

  return (
    <Layout>
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(articleLd) }} />
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(breadcrumbLd) }} />
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(faqLd) }} />

      {/* ヒーロー — 全幅ビルボード */}
      <section className="relative bg-[#1a2942] overflow-hidden min-h-[520px] lg:min-h-[600px] flex items-center">
        <img
          src="/images/generated/jba-butler-profession-hero.jpg"
          alt="静謐な邸内で周囲を見渡し、危機に備える執事の佇まい"
          className="absolute inset-0 w-full h-full object-cover object-center"
        />
        <div className="absolute inset-0 bg-[linear-gradient(90deg,rgba(16,20,26,0.92)_0%,rgba(16,20,26,0.72)_48%,rgba(16,20,26,0.35)_100%)]"></div>
        <div className="relative z-10 w-full max-w-[1680px] mx-auto px-4 sm:px-6 lg:px-10 pt-32 pb-16 lg:pt-36 lg:pb-20">
          <div className="max-w-[720px]">
            <span className="block font-['Playfair_Display',serif] uppercase tracking-[0.32em] text-xs sm:text-[13px] text-[#d7b779] mb-4">
              Crisis Standards
            </span>
            <div className="w-12 h-px bg-[#a38e65] mb-6"></div>
            <h1 className="jba-display font-normal text-white text-[clamp(28px,3.4vw,44px)] leading-[1.4] tracking-[0.04em] mb-6">
              <span className="block">執事の危機管理指針</span>
              <span className="block text-[#d7b779]">初動と保全の基準</span>
            </h1>
            <p className="font-['Noto_Serif_JP',serif] text-base sm:text-lg text-white/90 leading-[1.95]">
              一般社団法人 全日本執事協会は、1890年（明治23年）の創設以来、日本における執事文化の継承に取り組んできました。代表理事・石井裕一（三代目頭取）のもと、本指針は認定団体である当協会が定める危機管理の基準として、想定事態・初動の優先順位・不在時の判断・連絡と記録・平時の訓練・事後の振り返りを示します。危機対応は英雄的な独断ではなく、依頼者と場の安全を守るための静かな手順です。
            </p>
          </div>
        </div>
      </section>

      <div className="bg-white [&>div]:!pt-6">
        <Breadcrumb items={breadcrumbs} />
      </div>

      <div className="bg-white">
        <div className="max-w-[1100px] mx-auto px-4 sm:px-6 lg:px-10 py-12 sm:py-16 lg:py-20 space-y-20 sm:space-y-24">

          {/* S1. 基準化の理由 — 左番号帯＋本文（groomingの引用帯型とは別） */}
          <section>
            <div className="grid lg:grid-cols-[120px_1fr] gap-8 lg:gap-12 items-start">
              <div className="hidden lg:block">
                <span className="block font-['Playfair_Display',serif] text-[64px] leading-none text-[#a38e65]/35">01</span>
                <span className="block mt-3 font-['Playfair_Display',serif] uppercase tracking-[0.2em] text-[11px] text-[#8f7a56]">Why</span>
              </div>
              <div>
                <span className="block lg:hidden font-['Playfair_Display',serif] uppercase tracking-[0.24em] text-[11px] text-[#8f7a56] mb-3">
                  I. Why Standards
                </span>
                <h2 className="jba-display font-medium text-[#1a2942] text-2xl sm:text-3xl lg:text-[31px] leading-[1.5] tracking-[0.02em] mb-5">
                  危機管理を<span className="text-[#a38e65]">基準化する理由</span>
                </h2>
                <div className="space-y-[18px] font-['Noto_Serif_JP',serif] text-base text-[#333] leading-[1.95]">
                  <p>
                    邸宅や同行先で起きる急変は、毎回条件が異なります。だからこそ、個人の経験や気質だけに委ねると、初動の速さも報告の粒度もばらつきます。基準化の目的は、恐れを煽ることではなく、誰が当番でも同じ優先順位で動ける座標を共有することにあります。
                  </p>
                  <p>
                    当協会は、Associate・Professional・Masterの三段階と専門資格を通じて判断力を育てると同時に、現場で迷わない危機対応の骨子を本指針にまとめています。行動の日々の型は
                    <a href="/standards/conduct" className="text-[#1a2942] underline underline-offset-4 decoration-[#a38e65] mx-1">執事の行動規範</a>
                    と、情報の扱い方は
                    <a href="/standards/infosec" className="text-[#1a2942] underline underline-offset-4 decoration-[#a38e65] mx-1">情報セキュリティの基準</a>
                    と併せて参照し、基準全体の見取り図は
                    <a href="/standards" className="text-[#1a2942] underline underline-offset-4 decoration-[#a38e65] mx-1">基準・指針</a>
                    から俯瞰できます。
                  </p>
                  <p>
                    危機管理は、特別な日のための別技能ではありません。平時の観察・点検・短い申し送りが、そのまま有事の初動になります。本指針は「何を急ぐか」「何をしないか」を先に定め、依頼者が不在でも場が破綻しないための共通言語を示します。
                  </p>
                </div>
              </div>
            </div>
          </section>

          {/* S2. 想定する事態 — 五枚の縦スタック＋右に画像 */}
          <section>
            <span className="block font-['Playfair_Display',serif] uppercase tracking-[0.24em] text-[11px] text-[#8f7a56] mb-3">
              II. Anticipated Situations
            </span>
            <h2 className="jba-display font-medium text-[#1a2942] text-2xl sm:text-3xl lg:text-[31px] leading-[1.5] tracking-[0.02em] mb-5">
              想定する事態
            </h2>
            <p className="font-['Noto_Serif_JP',serif] text-base text-[#333] leading-[1.95] mb-8 max-w-3xl">
              すべてを網羅リストにすることはできません。それでも、急病・火災・不審者・停電・水漏れは、邸宅奉仕で繰り返し想定すべき代表的な軸です。各事態で共通するのは、執事が専門職の領域を越えないこと、そして観察した事実を正確に渡すことです。
            </p>
            <div className="grid lg:grid-cols-[1fr_320px] gap-8 items-start mb-8">
              <div className="divide-y divide-[#e2ddd2] border border-[#e2ddd2] bg-[#fbfaf7]">
                {situationAxes.map((item) => (
                  <div key={item.en} className="px-6 sm:px-7 py-5">
                    <div className="flex flex-wrap items-baseline gap-x-4 gap-y-1 mb-2">
                      <span className="font-['Playfair_Display',serif] italic text-[#8f7a56] text-[12px] tracking-[0.08em]">{item.en}</span>
                      <h3 className="jba-display font-medium text-[#1a2942] text-lg">{item.ja}</h3>
                    </div>
                    <p className="font-['Noto_Serif_JP',serif] text-sm text-[#4a4638] leading-[1.9]">{item.text}</p>
                  </div>
                ))}
              </div>
              <div className="space-y-5">
                <img
                  src="/images/generated/jba-butler-team-briefing.jpg"
                  alt="想定事態と初動の役割を共有するチームのブリーフィング"
                  className="w-full h-56 object-cover border border-[#a38e65]/30"
                />
                <div className="bg-[#1a2942] px-5 py-5">
                  <p className="font-['Noto_Serif_JP',serif] text-sm text-[#e7e2d8] leading-[1.9]">
                    想定は恐怖のためではなく、言葉を揃えるためです。同じ言葉で通報し、同じ順で退避できれば、現場の混乱は静かに小さくなります。
                  </p>
                </div>
              </div>
            </div>
          </section>

          {/* S3. 初動の優先順位 — 横並び四段＋下に補足画像 */}
          <section>
            <span className="block font-['Playfair_Display',serif] uppercase tracking-[0.24em] text-[11px] text-[#8f7a56] mb-3">
              III. First Response
            </span>
            <h2 className="jba-display font-medium text-[#1a2942] text-2xl sm:text-3xl lg:text-[31px] leading-[1.5] tracking-[0.02em] mb-3">
              初動の優先順位——人命から報告へ
            </h2>
            <p className="font-['Noto_Serif_JP',serif] text-base text-[#333] leading-[1.95] mb-8 max-w-3xl">
              危機の瞬間に手順書を開き直す余裕は、ほとんどありません。だからこそ、優先順位だけは身体に染み込ませます。人命の安全、通報と取次、被害の保全、報告と共有——この四段を飛ばさず、混ぜないことが本指針の骨格です。
            </p>
            <div className="bg-[#1a2942] px-6 sm:px-8 py-4 mb-0 flex flex-wrap items-center gap-x-8 gap-y-2">
              <span className="font-['Playfair_Display',serif] text-[#d7b779] tracking-[0.18em] text-sm">LIFE → ALERT → CONTAIN → REPORT</span>
              <span className="font-['Noto_Serif_JP',serif] text-[#f8f6f0]/85 text-sm tracking-[0.06em]">
                順を守ることが、最大の速度になる
              </span>
            </div>
            <div className="grid sm:grid-cols-2 border border-t-0 border-[#e2ddd2] bg-white mb-8">
              {prioritySteps.map((item) => (
                <div key={item.no} className="px-6 sm:px-8 py-6 border-b sm:border-r border-[#e2ddd2] odd:sm:border-r even:sm:border-r-0">
                  <span className="font-['Playfair_Display',serif] text-[#d7b779] text-2xl block mb-2">{item.no}</span>
                  <h3 className="jba-display font-medium text-[#1a2942] text-lg mb-2">{item.title}</h3>
                  <p className="font-['Noto_Serif_JP',serif] text-[15px] text-[#4a4638] leading-[1.9]">{item.text}</p>
                </div>
              ))}
            </div>
            <div className="grid lg:grid-cols-[1fr_300px] gap-8 items-center">
              <p className="font-['Noto_Serif_JP',serif] text-base text-[#333] leading-[1.95]">
                物を守る気持ちが先に立つと、人の退避が遅れます。逆に、報告を急ぐあまり現場の観察が雑になると、到着した支援へ渡す言葉が揺れます。四段は優劣の装飾ではなく、時間軸上の順番です。一つを終えてから次へ進む——その単調さが、結果として最も速い保全につながります。
              </p>
              <img
                src="/images/generated/jba-butler-quality-control.jpg"
                alt="初動後に状況と記録の精度を点検する執事"
                className="w-full h-48 lg:h-52 object-cover border border-[#a38e65]/30"
              />
            </div>
          </section>

          {/* S4. 依頼者不在時 — 三列カード */}
          <section>
            <span className="block font-['Playfair_Display',serif] uppercase tracking-[0.24em] text-[11px] text-[#8f7a56] mb-3">
              IV. Absence Judgment
            </span>
            <h2 className="jba-display font-medium text-[#1a2942] text-2xl sm:text-3xl lg:text-[31px] leading-[1.5] tracking-[0.02em] mb-5">
              依頼者が不在のときの判断
            </h2>
            <div className="bg-[#fbfaf7] border-l-2 border-[#a38e65] px-6 sm:px-8 py-6 mb-8">
              <p className="font-['Noto_Serif_JP',serif] text-[15px] sm:text-base text-[#333] leading-[1.95]">
                不在は、権限の空白ではありません。あらかじめ交わした委任の範囲と、第二・第三の連絡先が、その場の判断を支えます。連絡が取れない焦りから独断の領域を広げるのではなく、「今すぐ守るべき安全」と「承認を待つべき変更」を切り分けることが職能です。
              </p>
            </div>
            <div className="grid lg:grid-cols-3 gap-6 mb-8">
              {absenceGuides.map((block) => (
                <div key={block.label} className="bg-white border border-[#e2ddd2] shadow-[0_12px_40px_rgba(26,41,66,0.06)] p-7">
                  <h3 className="jba-display font-medium text-[#1a2942] text-lg mb-4 pb-4 border-b border-[#e2ddd2]">{block.label}</h3>
                  <ul className="space-y-3">
                    {block.lines.map((line) => (
                      <li key={line} className="flex gap-3 font-['Noto_Serif_JP',serif] text-[14px] text-[#333] leading-[1.85]">
                        <span className="text-[#a38e65] shrink-0 mt-0.5">▸</span>
                        <span>{line}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              ))}
            </div>
            <img
              src="/images/generated/jba-become-foundation-training.jpg"
              alt="不在時の判断範囲を研修で確認する基礎的な学びの場"
              className="w-full h-56 sm:h-64 object-cover border border-[#a38e65]/30"
            />
          </section>

          {/* S5. 連絡系統と記録 — 三分割トップ＋本文 */}
          <section>
            <span className="block font-['Playfair_Display',serif] uppercase tracking-[0.24em] text-[11px] text-[#8f7a56] mb-3">
              V. Channels & Records
            </span>
            <h2 className="jba-display font-medium text-[#1a2942] text-2xl sm:text-3xl lg:text-[31px] leading-[1.5] tracking-[0.02em] mb-5">
              連絡系統と記録の残し方
            </h2>
            <div className="grid md:grid-cols-3 gap-0 mb-10 border-t-2 border-[#1a2942]">
              {recordPoints.map((r) => (
                <div key={r.en} className="border-b md:border-b-0 md:border-r last:border-r-0 border-[#e2ddd2] px-5 py-6">
                  <span className="block font-['Playfair_Display',serif] text-[#a38e65] text-sm tracking-[0.12em] mb-2">{r.en}</span>
                  <h3 className="jba-display font-medium text-[#1a2942] text-lg mb-3">{r.ja}</h3>
                  <p className="font-['Noto_Serif_JP',serif] text-sm text-[#4a4638] leading-[1.9]">{r.text}</p>
                </div>
              ))}
            </div>
            <div className="grid lg:grid-cols-[300px_1fr] gap-8 items-start">
              <img
                src="/images/generated/jba-become-professional-practice.jpg"
                alt="連絡と記録の手順を実務のなかで確認する執事"
                className="w-full h-52 object-cover border border-[#a38e65]/30"
              />
              <div className="font-['Noto_Serif_JP',serif] text-base text-[#333] leading-[1.95] space-y-[18px]">
                <p>
                  記録は、責任を押し付けるための文書ではありません。後から入った支援者・家族・業者が、同じ地図を見て動くための共有材です。手書きでも端末でも構いませんが、紛失しにくい場所と、閲覧できる人の範囲を平時に決めておきます。
                </p>
                <p>
                  個人情報や邸内の詳細が含まれる場合は、共有の経路そのものを最小限にします。危機だからといって私的なメッセージアプリへ無秩序に転送しない——その節度は、情報面の基準と一体で守ります。事実のみを残し、評価や憶測で埋めないことが、信頼を保つ書き方です。
                </p>
              </div>
            </div>
          </section>

          {/* S6. 平時の訓練と設備点検 — 紺地パネル内四分割 */}
          <section>
            <span className="block font-['Playfair_Display',serif] uppercase tracking-[0.24em] text-[11px] text-[#8f7a56] mb-3">
              VI. Drill & Inspection
            </span>
            <h2 className="jba-display font-medium text-[#1a2942] text-2xl sm:text-3xl lg:text-[31px] leading-[1.5] tracking-[0.02em] mb-6">
              平時の訓練と設備点検
            </h2>
            <div className="bg-[#1a2942] p-8 sm:p-10 lg:p-12 mb-8">
              <p className="font-['Noto_Serif_JP',serif] text-base text-[#e7e2d8] leading-[1.95] mb-8 max-w-3xl">
                有事の品質は、平時の単調な確認で決まります。大掛かりな演習だけが訓練ではありません。経路を歩く、懐中電灯を点ける、連絡先を声に出す、止水位置を指差す——短い反復が、当日の手足を止めなくします。
              </p>
              <div className="grid sm:grid-cols-2 gap-6">
                {peacetimeDrills.map((d) => (
                  <div key={d.en} className="border border-[#a38e65]/35 px-5 py-5">
                    <span className="block font-['Playfair_Display',serif] text-[#d7b779] text-sm tracking-[0.12em] mb-2">{d.en}</span>
                    <h3 className="jba-display font-medium text-[#f8f6f0] text-lg mb-3">{d.ja}</h3>
                    <p className="font-['Noto_Serif_JP',serif] text-sm text-[#e7e2d8]/90 leading-[1.9]">{d.text}</p>
                  </div>
                ))}
              </div>
            </div>
            <p className="font-['Noto_Serif_JP',serif] text-base text-[#333] leading-[1.95]">
              設備点検は、チェック表を埋める作業で終わらせません。期限切れ・電池切れ・塞がれた経路・貼り紙の剥がれなど、「いつか直す」が積もった箇所こそ、有事に表面化します。発見した不備は、担当と期限を付けて申し送り、閉じるまで一覧に残します。Master Butlerが示す統括も、こうした日常の信号をチームの型へ戻すところにあります。
            </p>
          </section>

          {/* S7. 事後の振り返り＋FAQ — 振り返りリストと問いを同一セクションに統合 */}
          <section>
            <span className="block font-['Playfair_Display',serif] uppercase tracking-[0.24em] text-[11px] text-[#8f7a56] mb-3">
              VII. Review & Questions
            </span>
            <h2 className="jba-display font-medium text-[#1a2942] text-2xl sm:text-3xl lg:text-[31px] leading-[1.5] tracking-[0.02em] mb-5">
              事後の振り返りと基準への反映
            </h2>
            <div className="grid lg:grid-cols-[1fr_300px] gap-10 items-start mb-12">
              <div>
                <p className="font-['Noto_Serif_JP',serif] text-base text-[#333] leading-[1.95] mb-6">
                  事態が収束したあとこそ、基準は更新されます。うまくいった点を美談で終わらせず、遅れた分岐を個人の責任に帰さず、手順・備品・連絡先のどれを直すかに落とし込みます。振り返りは追及の場ではなく、次の初動を一段静かにするための作業です。
                </p>
                <ul className="space-y-4 mb-8">
                  {reviewLoops.map((line) => (
                    <li key={line} className="flex gap-3 font-['Noto_Serif_JP',serif] text-[15px] text-[#333] leading-[1.85]">
                      <span className="text-[#a38e65] shrink-0">—</span>
                      <span>{line}</span>
                    </li>
                  ))}
                </ul>
                <p className="font-['Noto_Serif_JP',serif] text-base text-[#333] leading-[1.95]">
                  家ごとの手順書と本指針のあいだに差が生まれたときは、現場の事実を優先して文言を整えます。資格の段階を問わず、同じ事例を短い教材として共有することで、個人の記憶が組織の判断へ変わっていきます。
                </p>
              </div>
              <img
                src="/images/generated/jba-become-master-leadership.jpg"
                alt="事後の振り返りを率い、基準への反映を示す上級執事"
                className="w-full h-56 lg:h-[320px] object-cover border border-[#a38e65]/30"
              />
            </div>
            <div className="border-t-2 border-[#1a2942]">
              <p className="font-['Playfair_Display',serif] uppercase tracking-[0.18em] text-[12px] text-[#8f7a56] pt-6 mb-4">
                Questions
              </p>
              {faqItems.map((item) => (
                <div key={item.q} className="border-b border-[#e2ddd2] py-6">
                  <h3 className="jba-display font-medium text-[#1a2942] text-lg mb-3 flex gap-3">
                    <span className="text-[#a38e65] font-['Playfair_Display',serif] shrink-0">Q</span>
                    <span>{item.q}</span>
                  </h3>
                  <p className="font-['Noto_Serif_JP',serif] text-[15px] text-[#333] leading-[1.95] pl-8">
                    {item.a}
                  </p>
                </div>
              ))}
            </div>
          </section>

          {/* S8. 関連導線 — 9th section, 8th h2 */}
          <section>
            <span className="block font-['Playfair_Display',serif] uppercase tracking-[0.24em] text-[11px] text-[#8f7a56] mb-3">
              VIII. Related Paths
            </span>
            <h2 className="jba-display font-medium text-[#1a2942] text-2xl sm:text-3xl lg:text-[31px] leading-[1.5] tracking-[0.02em] mb-6">
              関連する基準と学びへ
            </h2>
            <div className="bg-[#1a2942] border border-[#a38e65]/45 p-8 sm:p-10 lg:p-12">
              <p className="font-['Noto_Serif_JP',serif] text-base sm:text-lg text-[#e7e2d8] leading-[1.95] max-w-3xl mb-8">
                危機管理指針は、単独で完結するものではありません。日々の行動規範で判断の节度を整え、情報の扱いを守り、資格制度を通じて段階的に再現性を育てることで、有事の品位が続きます。全日本執事協会は、1890年（明治23年）から続く系譜のうえで、いまの暮らしにふさわしい奉仕の座標を示し続けます。
              </p>
              <div className="flex flex-col sm:flex-row flex-wrap gap-4 sm:gap-6">
                <a href="/standards/conduct" className={BTN08_ON_NAVY}>
                  執事の行動規範
                </a>
                <a href="/standards/infosec" className={BTN08_ON_NAVY}>
                  情報セキュリティの基準
                </a>
                <a href="/qualifications" className={BTN08_ON_NAVY}>
                  資格制度を見る
                </a>
              </div>
              <div className="mt-6">
                <a href="/standards" className={BTN08_ON_LIGHT}>
                  基準・指針の一覧
                </a>
              </div>
            </div>
          </section>

        </div>
      </div>
    </Layout>
  );
}
